"use client";

import { c as _c } from "react/compiler-runtime";
import { ChainBadge, ChainBadgeFragment, CollectionLockup, CollectionLockupContent, CollectionLockupFragment, CollectionLockupTitle } from "@opensea/app-kit";
import { graphql, readFragment } from "@opensea/graphql";
import { EM_DASH, useDateTimeFormatter } from "@opensea/i18n";
import { HandoffButton } from "@opensea/navigation";
import { Item, ItemContent, ItemDescription, ItemTitle } from "@opensea/next-ui-kit";
import { Checkbox, classNames, FlexCenter, NumberDisplay, TABLE_SIZES, TableCell, TableRow, Tooltip, Truncate, tableRowSizeVariants, useTable } from "@opensea/ui-kit";
import { useSelectItemMouseShortcuts } from "@opensea/ui-kit/hooks";
import { useContextSelector } from "use-context-selector";
import { useShallow } from "zustand/react/shallow";
import { CollectionPreviewTooltip } from "@/components/collection/CollectionPreviewTooltip/CollectionPreviewTooltip";
import { CollectionPreviewTooltipFragment } from "@/components/collection/CollectionPreviewTooltip/CollectionPreviewTooltip.fragment";
import { TokenPrice } from "@/components/price/TokenPrice";
import { TokenPriceFragment } from "@/components/price/TokenPrice.fragment";
import { useIsOwnProfileAddress } from "@/components/profile/ProfileAddressProvider";
import { ProfileSettingsContext } from "@/components/profile/ProfileSettingsProvider";
import { EnforcementBadge, EnforcementBadgeFragment } from "@/components/trustandsafety/EnforcementBadge";
import { useRowHighlightClassNames } from "@/hooks/use-row-highlight";
import { ItemAvatar, ItemAvatarFragment } from "../../avatar/ItemAvatar";
import { BulkActionsDisabledTooltip, BulkActionsDisabledTooltipFragment } from "../../BulkActionsDisabledTooltip";
import { CollectionLink } from "../../collection/CollectionLink";
import { CollectionLinkFragment } from "../../collection/CollectionLink.fragment";
import { ItemLink, ItemLinkFragment } from "../../ItemLink";
import { ItemPreviewTooltip } from "../../ItemPreviewTooltip/ItemPreviewTooltip";
import { ItemPreviewTooltipFragment } from "../../ItemPreviewTooltip/ItemPreviewTooltip.fragment";
import { OwnedQuantityBadge, OwnedQuantityFragment } from "../../OwnedQuantityBadge";
import { RarityTooltip, RarityTooltipFragment } from "../../rarity/RarityTooltip";
import { SellItemTableButton, SellItemTableButtonFragment } from "../../SellItemTableButton";
import { useProfileItemsSelectionStore } from "../ProfileItemsSelectionProvider";
import { profileItemsSelectionFragment } from "../profile-items-selection-store";
import { ProfileItemsTableSkeletonRow } from "./ProfileItemsTableSkeletonRow";
import { PROFILE_ITEMS_TABLE_COLUMN_CLASSNAMES } from "./styles";
export const ProfileItemsTableRowFragment = graphql(`
    fragment ProfileItemsTableRowFragment on Item {
      id
      chain {
        ...ChainBadge
      }
      isFungible
      name
      lastTransferAt
      ownership(address: $address) {
        id
      }
      rarity {
        rank
        category
      }
      collection {
        floorPrice {
          pricePerItem {
            ...TokenPrice
          }
        }
        ...CollectionLink
        ...CollectionPreviewTooltip
        ...CollectionLockup
      }
      enforcement {
        isCompromised
      }
      lowestListingForOwner(address: $address) {
        pricePerItem {
          ...TokenPrice
        }
      }
      lastSale {
        ...TokenPrice
      }
      standard
      ...SellItemTableButton
      ...ItemLink
      ...ItemAvatar
      ...profileItemsSelection
      ...EnforcementBadge
      ...OwnedQuantity
      ...RarityTooltip
      ...ItemPreviewTooltip
      ...BulkActionsDisabledTooltip
    }
  `, [ChainBadgeFragment, ItemLinkFragment, TokenPriceFragment, profileItemsSelectionFragment, ItemAvatarFragment, EnforcementBadgeFragment, OwnedQuantityFragment, RarityTooltipFragment, CollectionLinkFragment, CollectionLockupFragment, SellItemTableButtonFragment, ItemPreviewTooltipFragment, CollectionPreviewTooltipFragment, BulkActionsDisabledTooltipFragment]);
export function ProfileItemsTableRow(t0) {
  const $ = _c(134);
  const {
    item: itemKey,
    index
  } = t0;
  const dateFormat = useDateTimeFormatter();
  const {
    size
  } = useTable();
  const [selectedItemsMap, selectItem, allItems, isItemSelectable] = useProfileItemsSelectionStore(useShallow(_temp));
  const item = readFragment(ProfileItemsTableRowFragment, itemKey);
  const isOwnProfileAddress = useIsOwnProfileAddress();
  let t1;
  if ($[0] !== allItems || $[1] !== selectItem || $[2] !== selectedItemsMap) {
    t1 = {
      items: allItems,
      selectedItems: selectedItemsMap,
      selectItem
    };
    $[0] = allItems;
    $[1] = selectItem;
    $[2] = selectedItemsMap;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    handleOnClick
  } = useSelectItemMouseShortcuts(t1);
  const {
    showChainIcon,
    showRarity
  } = useContextSelector(ProfileSettingsContext, _temp2);
  const highlightClassNames = useRowHighlightClassNames(item?.id);
  if (!item) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <ProfileItemsTableSkeletonRow />;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  const isSelected = selectedItemsMap.has(item.id);
  const disabled = isOwnProfileAddress ? !(isSelected || isItemSelectable(item.id)) : true;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let T5;
  let T6;
  let T7;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== disabled || $[6] !== handleOnClick || $[7] !== highlightClassNames || $[8] !== index || $[9] !== isOwnProfileAddress || $[10] !== isSelected || $[11] !== item || $[12] !== showChainIcon || $[13] !== size) {
    const {
      image,
      text
    } = tableRowSizeVariants({
      size
    });
    const {
      badge: badgeSize
    } = TABLE_SIZES[size];
    const showChainBadge = Boolean(badgeSize) && showChainIcon;
    T7 = HandoffButton;
    T6 = ItemLink;
    t15 = item;
    T5 = TableRow;
    t11 = highlightClassNames;
    t12 = !disabled;
    if ($[36] !== disabled || $[37] !== handleOnClick || $[38] !== index) {
      t13 = e => {
        if (disabled) {
          return;
        }
        handleOnClick(e, index);
      };
      $[36] = disabled;
      $[37] = handleOnClick;
      $[38] = index;
      $[39] = t13;
    } else {
      t13 = $[39];
    }
    if ($[40] !== disabled || $[41] !== isOwnProfileAddress || $[42] !== isSelected || $[43] !== item) {
      t14 = isOwnProfileAddress ? <TableCell className={PROFILE_ITEMS_TABLE_COLUMN_CLASSNAMES.checkbox}><BulkActionsDisabledTooltip disabled={disabled} item={item}><Checkbox checked={isSelected} disabled={disabled} /></BulkActionsDisabledTooltip></TableCell> : null;
      $[40] = disabled;
      $[41] = isOwnProfileAddress;
      $[42] = isSelected;
      $[43] = item;
      $[44] = t14;
    } else {
      t14 = $[44];
    }
    T4 = TableCell;
    t10 = PROFILE_ITEMS_TABLE_COLUMN_CLASSNAMES.item;
    T3 = ItemLink;
    t7 = "w-full";
    t8 = item;
    if ($[45] !== isOwnProfileAddress) {
      t9 = e_0 => {
        if (isOwnProfileAddress) {
          e_0.stopPropagation();
        }
      };
      $[45] = isOwnProfileAddress;
      $[46] = t9;
    } else {
      t9 = $[46];
    }
    T2 = HandoffButton;
    T1 = Item;
    t4 = "w-auto max-w-full";
    t5 = "unstyled";
    let t16;
    if ($[47] !== badgeSize || $[48] !== item.chain || $[49] !== showChainBadge) {
      t16 = showChainBadge ? <ChainBadge chain={item.chain} size={badgeSize} /> : undefined;
      $[47] = badgeSize;
      $[48] = item.chain;
      $[49] = showChainBadge;
      $[50] = t16;
    } else {
      t16 = $[50];
    }
    const t17 = <ItemAvatar badge={t16} className={classNames(image(), "aspect-square h-full shrink-0")} item={item} size={TABLE_SIZES[size].image} />;
    if ($[51] !== item || $[52] !== t17) {
      t6 = <ItemPreviewTooltip item={item}>{t17}</ItemPreviewTooltip>;
      $[51] = item;
      $[52] = t17;
      $[53] = t6;
    } else {
      t6 = $[53];
    }
    T0 = ItemContent;
    const t18 = <ItemTitle className={classNames(text(), "font-normal")}>{item.name}</ItemTitle>;
    let t19;
    if ($[54] !== t18) {
      t19 = <Truncate>{t18}</Truncate>;
      $[54] = t18;
      $[55] = t19;
    } else {
      t19 = $[55];
    }
    let t20;
    if ($[56] !== item) {
      t20 = item.isFungible ? <OwnedQuantityBadge className="py-0" iconSize={10} item={item} /> : null;
      $[56] = item;
      $[57] = t20;
    } else {
      t20 = $[57];
    }
    let t21;
    if ($[58] !== t19 || $[59] !== t20) {
      t21 = <FlexCenter className="max-w-full gap-1">{t19}{t20}</FlexCenter>;
      $[58] = t19;
      $[59] = t20;
      $[60] = t21;
    } else {
      t21 = $[60];
    }
    if ($[61] !== item || $[62] !== t21) {
      t2 = <ItemPreviewTooltip item={item}>{t21}</ItemPreviewTooltip>;
      $[61] = item;
      $[62] = t21;
      $[63] = t2;
    } else {
      t2 = $[63];
    }
    t3 = item.collection !== null && size !== "sm" && size !== "xs" ? <CollectionPreviewTooltip collection={item.collection}><ItemDescription className={classNames(text(), "max-w-full")}><CollectionLink collection={item.collection} onClick={_temp3}><CollectionLockup collection={item.collection} size={size}><CollectionLockupContent><CollectionLockupTitle color="text-secondary" containerClassName="w-full" disableTextOverflowTooltip={true} weight="regular" /></CollectionLockupContent></CollectionLockup></CollectionLink></ItemDescription></CollectionPreviewTooltip> : null;
    $[5] = disabled;
    $[6] = handleOnClick;
    $[7] = highlightClassNames;
    $[8] = index;
    $[9] = isOwnProfileAddress;
    $[10] = isSelected;
    $[11] = item;
    $[12] = showChainIcon;
    $[13] = size;
    $[14] = T0;
    $[15] = T1;
    $[16] = T2;
    $[17] = T3;
    $[18] = T4;
    $[19] = T5;
    $[20] = T6;
    $[21] = T7;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
    $[28] = t2;
    $[29] = t3;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
  } else {
    T0 = $[14];
    T1 = $[15];
    T2 = $[16];
    T3 = $[17];
    T4 = $[18];
    T5 = $[19];
    T6 = $[20];
    T7 = $[21];
    t10 = $[22];
    t11 = $[23];
    t12 = $[24];
    t13 = $[25];
    t14 = $[26];
    t15 = $[27];
    t2 = $[28];
    t3 = $[29];
    t4 = $[30];
    t5 = $[31];
    t6 = $[32];
    t7 = $[33];
    t8 = $[34];
    t9 = $[35];
  }
  let t16;
  if ($[64] !== T0 || $[65] !== t2 || $[66] !== t3) {
    t16 = <T0>{t2}{t3}</T0>;
    $[64] = T0;
    $[65] = t2;
    $[66] = t3;
    $[67] = t16;
  } else {
    t16 = $[67];
  }
  let t17;
  if ($[68] !== T1 || $[69] !== t16 || $[70] !== t4 || $[71] !== t5 || $[72] !== t6) {
    t17 = <T1 className={t4} variant={t5}>{t6}{t16}</T1>;
    $[68] = T1;
    $[69] = t16;
    $[70] = t4;
    $[71] = t5;
    $[72] = t6;
    $[73] = t17;
  } else {
    t17 = $[73];
  }
  let t18;
  if ($[74] !== T2 || $[75] !== t17) {
    t18 = <T2>{t17}</T2>;
    $[74] = T2;
    $[75] = t17;
    $[76] = t18;
  } else {
    t18 = $[76];
  }
  let t19;
  if ($[77] !== T3 || $[78] !== t18 || $[79] !== t7 || $[80] !== t8 || $[81] !== t9) {
    t19 = <T3 className={t7} item={t8} onClick={t9}>{t18}</T3>;
    $[77] = T3;
    $[78] = t18;
    $[79] = t7;
    $[80] = t8;
    $[81] = t9;
    $[82] = t19;
  } else {
    t19 = $[82];
  }
  let t20;
  if ($[83] !== T4 || $[84] !== t10 || $[85] !== t19) {
    t20 = <T4 className={t10}>{t19}</T4>;
    $[83] = T4;
    $[84] = t10;
    $[85] = t19;
    $[86] = t20;
  } else {
    t20 = $[86];
  }
  const t21 = item.lowestListingForOwner?.pricePerItem;
  let t22;
  if ($[87] !== t21) {
    t22 = <TableCell className={PROFILE_ITEMS_TABLE_COLUMN_CLASSNAMES.listingPrice}><TokenPrice price={t21} /></TableCell>;
    $[87] = t21;
    $[88] = t22;
  } else {
    t22 = $[88];
  }
  let t23;
  if ($[89] !== item || $[90] !== showRarity) {
    t23 = showRarity ? <TableCell className={PROFILE_ITEMS_TABLE_COLUMN_CLASSNAMES.rarity}><RarityTooltip item={item}><NumberDisplay display="full" prefix="#" value={item?.rarity?.rank} /></RarityTooltip></TableCell> : null;
    $[89] = item;
    $[90] = showRarity;
    $[91] = t23;
  } else {
    t23 = $[91];
  }
  const t24 = item.collection?.floorPrice?.pricePerItem;
  let t25;
  if ($[92] !== t24) {
    t25 = <TableCell className={PROFILE_ITEMS_TABLE_COLUMN_CLASSNAMES.floor}><TokenPrice price={t24} /></TableCell>;
    $[92] = t24;
    $[93] = t25;
  } else {
    t25 = $[93];
  }
  let t26;
  if ($[94] !== item || $[95] !== size) {
    t26 = <TableCell className={PROFILE_ITEMS_TABLE_COLUMN_CLASSNAMES.bestOffer}>{item.enforcement.isCompromised ? <NumberDisplay display="full" /> : <EnforcementBadge entity={item} renderWhenNotEnforced={<SellItemTableButton item={item} size={size === "md" ? "sm" : "xs"} />} />}</TableCell>;
    $[94] = item;
    $[95] = size;
    $[96] = t26;
  } else {
    t26 = $[96];
  }
  let t27;
  if ($[97] !== item.isFungible || $[98] !== item.lastSale) {
    t27 = item.isFungible ? EM_DASH : <TokenPrice price={item.lastSale} />;
    $[97] = item.isFungible;
    $[98] = item.lastSale;
    $[99] = t27;
  } else {
    t27 = $[99];
  }
  let t28;
  if ($[100] !== t27) {
    t28 = <TableCell className={PROFILE_ITEMS_TABLE_COLUMN_CLASSNAMES.cost}>{t27}</TableCell>;
    $[100] = t27;
    $[101] = t28;
  } else {
    t28 = $[101];
  }
  let t29;
  if ($[102] !== dateFormat || $[103] !== item.lastTransferAt) {
    t29 = dateFormat(item.lastTransferAt ? new Date(item.lastTransferAt) : undefined, {
      display: "datetime"
    });
    $[102] = dateFormat;
    $[103] = item.lastTransferAt;
    $[104] = t29;
  } else {
    t29 = $[104];
  }
  const t30 = !item.lastTransferAt;
  let t31;
  if ($[105] !== dateFormat || $[106] !== item.lastTransferAt) {
    t31 = dateFormat(item.lastTransferAt ? new Date(item.lastTransferAt) : undefined, {
      display: "date-numeric"
    });
    $[105] = dateFormat;
    $[106] = item.lastTransferAt;
    $[107] = t31;
  } else {
    t31 = $[107];
  }
  let t32;
  if ($[108] !== t31) {
    t32 = <span>{t31}</span>;
    $[108] = t31;
    $[109] = t32;
  } else {
    t32 = $[109];
  }
  let t33;
  if ($[110] !== t29 || $[111] !== t30 || $[112] !== t32) {
    t33 = <TableCell className={PROFILE_ITEMS_TABLE_COLUMN_CLASSNAMES.received}><Tooltip content={t29} disabled={t30}>{t32}</Tooltip></TableCell>;
    $[110] = t29;
    $[111] = t30;
    $[112] = t32;
    $[113] = t33;
  } else {
    t33 = $[113];
  }
  let t34;
  if ($[114] !== T5 || $[115] !== t11 || $[116] !== t12 || $[117] !== t13 || $[118] !== t14 || $[119] !== t20 || $[120] !== t22 || $[121] !== t23 || $[122] !== t25 || $[123] !== t26 || $[124] !== t28 || $[125] !== t33) {
    t34 = <T5 className={t11} interactive={t12} onClick={t13}>{t14}{t20}{t22}{t23}{t25}{t26}{t28}{t33}</T5>;
    $[114] = T5;
    $[115] = t11;
    $[116] = t12;
    $[117] = t13;
    $[118] = t14;
    $[119] = t20;
    $[120] = t22;
    $[121] = t23;
    $[122] = t25;
    $[123] = t26;
    $[124] = t28;
    $[125] = t33;
    $[126] = t34;
  } else {
    t34 = $[126];
  }
  let t35;
  if ($[127] !== T6 || $[128] !== t15 || $[129] !== t34) {
    t35 = <T6 item={t15}>{t34}</T6>;
    $[127] = T6;
    $[128] = t15;
    $[129] = t34;
    $[130] = t35;
  } else {
    t35 = $[130];
  }
  let t36;
  if ($[131] !== T7 || $[132] !== t35) {
    t36 = <T7>{t35}</T7>;
    $[131] = T7;
    $[132] = t35;
    $[133] = t36;
  } else {
    t36 = $[133];
  }
  return t36;
}
function _temp3(e_1) {
  e_1.stopPropagation();
}
function _temp2(state) {
  return {
    showChainIcon: state.showChainIcon,
    showRarity: state.showRarity
  };
}
function _temp(v) {
  return [v.selectedItemsMap, v.selectItem, v.allItems, v.isItemSelectable];
}